import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Mail, Phone, ArrowLeft, Truck, Package } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const OrderConfirmation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderId, reference, customerEmail } = location.state || {};

  // Redirect if no order data
  if (!orderId) {
    return (
  <div className="min-h-[80vh] md:min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            No Order Found
          </h2>
          <button
            onClick={() => navigate('/shop')}
            className="px-6 py-3 bg-mini-red text-white rounded-lg hover:bg-mini-red/90 transition-colors"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
  <div className="min-h-[80vh] md:min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle size={48} className="text-green-600 dark:text-green-400" />
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-3xl font-bold text-gray-900 dark:text-white mb-4"
          >
            Order Submitted Successfully!
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-lg text-gray-600 dark:text-gray-400 mb-8"
          >
            Thank you for your order. We'll process it once payment is confirmed.
          </motion.p>

          {/* Order Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-8"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Order Details
            </h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Order ID:</span>
                <span className="font-mono text-gray-900 dark:text-white">{orderId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Reference Number:</span>
                <span className="font-mono text-mini-red font-bold">{reference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Email:</span>
                <span className="text-gray-900 dark:text-white">{customerEmail}</span>
              </div>
            </div>
          </motion.div>

          {/* Next Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6 mb-8"
          >
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-4">
              What happens next?
            </h3>
            
            <div className="text-sm text-blue-800 dark:text-blue-300 space-y-2 text-left">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  1
                </div>
                <p>If you haven't uploaded proof of payment, please make your EFT payment using the banking details provided and email the proof to mike@silvertoncc.co.za</p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  2
                </div>
                <p>Our team will verify your payment and update your order status</p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  3
                </div>
                <p>We'll prepare your items and arrange PUDO delivery to your nearest collection point</p>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  4
                </div>
                <p>You'll receive tracking information and collection details via email</p>
              </div>
            </div>
          </motion.div>

          {/* PUDO Delivery Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6 mb-8"
          >
            <div className="flex items-center gap-2 text-green-800 dark:text-green-200 mb-3">
              <Truck size={20} />
              <h3 className="text-lg font-semibold">Delivery Information</h3>
            </div>
            
            <div className="text-sm text-green-700 dark:text-green-300 space-y-2">
              <div className="flex items-start gap-2">
                <Package size={16} className="mt-1 flex-shrink-0" />
                <p>
                  <strong>PUDO Delivery:</strong> Your order will be delivered via PUDO courier services to your nearest PUDO box or kiosk
                </p>
              </div>
              <div className="flex items-start gap-2">
                <Mail size={16} className="mt-1 flex-shrink-0" />
                <p>All tracking information and collection details will be emailed to: <span className="font-semibold">{customerEmail}</span></p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-1 flex-shrink-0" />
                <p>You'll receive an SMS notification when your order is ready for collection</p>
              </div>
              <div className="mt-4 p-3 bg-green-100 dark:bg-green-800/30 rounded-lg border border-green-300 dark:border-green-600">
                <p className="text-xs text-green-800 dark:text-green-200">
                  <strong>Alternative:</strong> If you prefer store collection instead of delivery, please contact us at mike@silvertoncc.co.za to arrange pickup from our Silverton workshop.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-mini-red/5 border border-mini-red/20 rounded-lg p-6 mb-8"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Need Help?
            </h3>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:mike@silvertoncc.co.za"
                className="inline-flex items-center gap-2 px-4 py-2 bg-mini-red text-white rounded-lg hover:bg-mini-red/90 transition-colors"
              >
                <Mail size={20} />
                Email Us
              </a>
              
              <a
                href="tel:+27729981365"
                className="inline-flex items-center gap-2 px-4 py-2 border border-mini-red text-mini-red rounded-lg hover:bg-mini-red/5 transition-colors"
              >
                <Phone size={20} />
                Call Us
              </a>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={() => navigate('/shop')}
              className="px-6 py-3 bg-mini-red text-white rounded-lg hover:bg-mini-red/90 transition-colors"
            >
              Continue Shopping
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft size={20} />
              Back to Home
            </button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
